import React, { FC } from 'react';

import { Button, Form, Layout, message, Row, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { NSKeys } from '@app/i18n';
import { getFormErrorsFromResponse } from '@app/services/getFormErrorsFromResponse';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { HeaderTitle } from '@components/HeaderTitle';

import { FormLimit, IFormValues, ISelectedProductLimit } from '../../../../components/LimitForm';
import { useCreateCatalogLimitQuery, useGetCatalogQuery } from '../../../../hooks';

export const Add: FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm<IFormValues>();
  const catalogsT = useTranslation(NSKeys.catalogs);
  const commonT = useTranslation(NSKeys.common);
  const validationT = useTranslation(NSKeys.validation);
  const { id } = useParams<{ id: string }>();
  const [selectedProducts, setSelectedProducts] = React.useState<ISelectedProductLimit[]>([]);

  const { data: catalog, isLoading: isCatalogLoading } = useGetCatalogQuery(id || '');
  const { mutate: createLimit, status, error } = useCreateCatalogLimitQuery();

  const goBack = () => navigate(`/dashboard/catalogs/${id}/limits`);

  React.useEffect(() => {
    if (status === 'success') {
      goBack();
    }
    if (status === 'error' && error) {
      const formValues = form.getFieldsValue();
      const { pointerErrors } = getFormErrorsFromResponse(formValues, error, validationT.t, true);

      message.error(pointerErrors.length ? pointerErrors?.[0]?.errors?.join(', ') : commonT.t('somethingWentWrong'));
    }
  }, [status, error]);

  const handleSubmit = (formValues: IFormValues) => {
    if (!id) {
      return;
    }
    if (selectedProducts.length === 0) {
      return message.error(validationT.t('limit_products_required'));
    }

    createLimit({
      catalogUid: id,
      data: {
        limit_owner_type: formValues.limit_owner_type,
        limit_owner_uid: String(formValues.limit_owner_uid.value),
        limit_type: 'quantity',
        items: selectedProducts.map((item) => ({
          product_uid: item.product_uid,
          limit_quantity: item.limit_quantity,
        })),
      },
    });
  };

  return (
    <Layout>
      <Layout.Header>
        <Breadcrumbs apiTitles={[{ id: ':catalogId', value: catalog?.name }]} />
        <Row justify="space-between" align="middle">
          <HeaderTitle title={catalogsT.t('limits.createTitle')} onClick={goBack} loading={isCatalogLoading} />
          <Space size="middle">
            <Button onClick={goBack} size="middle">
              {commonT.t('buttonCancel')}
            </Button>
            <Button
              type="primary"
              size="middle"
              form="createCatalogLimitForm"
              htmlType="submit"
              disabled={status === 'loading'}>
              {commonT.t('buttonSave')}
            </Button>
          </Space>
        </Row>
      </Layout.Header>

      <FormLimit
        selectedProductUids={selectedProducts}
        onSelectProductUids={setSelectedProducts}
        form={form}
        onFinish={handleSubmit}
        catalogUid={catalog?.uid}
      />
    </Layout>
  );
};
